import { useSelector } from 'react-redux';
import DataTable from '../DataTable';
import { toFixedTrunc, useJunoCommonFunctions } from '../../../client/Juno/helpers';
import CryptoPriceChange from './CryptoPriceChange';
import CryptoGraph from './CryptoGraph';
import { useEffect, useState } from 'react';
import CryptoBalanceExpand from './CryptoBalanceExpand';

// Utility functions for formatting
const formatPrice = price => {
    if (!price && price !== 0) return '$0.00';
    return `$${parseFloat(price.toFixed(2)).toLocaleString()}`;
};

const formatCurrency = (amount, symbol) => {
    if (!amount && amount !== 0) return '0.00';
    return `${toFixedTrunc(amount, 8)} ${symbol || ''}`;
};

const formatUSD = amount => {
    if (!amount && amount !== 0) return '0.00 USD';
    return `${toFixedTrunc(amount, 2)} USD`;
};

const formatPercentage = percentage => {
    if (!percentage && percentage !== 0) return '0.00%';
    return `${toFixedTrunc(percentage, 2)}%`;
};

// Cell renderers
const CellRenderer = {
    name: row => (
        <div className="flex items-center gap-2">
            <img src={row.icon} alt={`${row.name} icon`} className="w-6 h-6 rounded-full" />
            <span className="text-sm font-medium leading-tight">{row.name}</span>
        </div>
    ),

    current_price: row => (
        <span className="text-sm font-medium leading-tight">{formatPrice(row.current_price)}</span>
    ),

    balanceAmount: row => (
        <span className="text-sm font-medium leading-tight">
            {formatCurrency(row.balanceAmount, row.symbol)}
        </span>
    ),

    convertedUSDAmount: row => (
        <span className="text-sm font-medium leading-tight">
            {formatUSD(row.convertedUSDAmount)}
        </span>
    ),

    holdingPercentage: row => (
        <span className="text-sm font-medium leading-tight">
            {formatPercentage(row.holdingPercentage)}
        </span>
    ),

    price_change_percentage_24h: row => (
        <CryptoPriceChange value={row.price_change_percentage_24h} />
    ),

    trend: row => {
        return (
            <div className="-my-11 -mx-3 overflow-hidden z-1">
                <CryptoGraph name={`${row.name}-compact`} data={row.graphData} />
            </div>
        );
    },

    default: (row, key) => (
        <span className="text-sm font-medium leading-tight">{row[key] || '—'}</span>
    ),
};

// Table configuration
const createTableConfig = () => ({
    columns: [
        { key: 'name', label: 'Crypto Name' },
        { key: 'current_price', label: 'Price' },
        { key: 'price_change_percentage_24h', label: 'Change 24h' },
        { key: 'market_cap', label: 'Market Cap' },
        { key: 'trend', label: '30 day trend', noSort: true },
        { key: 'balanceAmount', label: 'Balance' },
        { key: 'convertedUSDAmount', label: 'Value' },
        { key: 'holdingPercentage', label: 'Portfolio' },
    ],

    customRenderCell: (col, row) => {
        const renderer = CellRenderer[col.key] || CellRenderer.default;
        return renderer(row, col.key);
    },
    hasNestedRows: true,
    renderNestedCryptoRow: (row, actionIcons) => {
        return (
            <CryptoBalanceExpand
                name={row.name}
                price={row.current_price}
                actionIcons={actionIcons}
                selected={row}
            />
        );
    },
});

const CryptoBalance = ({ actionIcons }) => {
    const cryptoStats = useSelector(state => state.juno.cryptoStats);
    const balancesCryptoData = useSelector(state => state.juno.balancesCrypto);
    const [cryptoGraphs, setCryptoGraphs] = useState([]);
    const activeCurrencyPage = useSelector((state) => state.juno.activeCurrencyPage)

    const image_base_url = useSelector(state => state.config.image_base_url);
    const { getCryptoGraphs } = useJunoCommonFunctions();

    const renderData = cryptoStats.map(crypto => {
        const balance =
            balancesCryptoData?.find(
                balance => balance?.currencyShortName == crypto?.symbol?.toUpperCase(),
            ) || {};

        const graphData =
            cryptoGraphs?.find(graph => {
                if (graph.coin == 'ripple') {
                    graph.coin = 'xrp';
                } else if (graph.coin == 'usd-coin') {
                    graph.coin = 'usdc';
                }

                return graph.coin == crypto.name?.toLowerCase();
            })?.trend || [];

        return {
            ...crypto,
            ...balance,
            icon: `${image_base_url}${balance?.currencyIcon}`,
            graphData,
        };
    });

    const overviewTableConfig = createTableConfig();

    const fetchCryptoGraphs = async () => {
        try {
            let graph = await getCryptoGraphs();
            setCryptoGraphs(graph);
        } catch (error) {
            console.error('failed to fetch graph', error);
        }
    };

    useEffect(() => {
        if(activeCurrencyPage == 'crypto') {
            fetchCryptoGraphs();
        }
    }, [activeCurrencyPage]);

    return (
        <div className="mb-1 px-1">
            <div
                className={`flex justify-between items-center  overflow-x-auto rounded-[8px] rounded-b-none border-[1px] border !border-section-br bg-modal-bg px-6 py-3`}>
                <p className="!text-section-title  text-sm font-semibold leading-5 uppercase ">
                    Balances
                </p>
            </div>
            <DataTable
                data={renderData}
                columns={overviewTableConfig.columns}
                hasNestedRows={true}
                renderNestedRow={row => overviewTableConfig.renderNestedCryptoRow(row, actionIcons)}
                customRenderCell={overviewTableConfig.customRenderCell}
            />
        </div>
    );
};

export default CryptoBalance;
